/**
 * @fileoverview System portal session-expired reauthentication screen.
 * @module system-web/auth
 *
 * Responsibilities:
 * - Present a graceful reauthentication prompt when MSAL requires user interaction.
 * - Keep expired-session messaging separate from authorization-denied messaging.
 *
 * Dependencies:
 * - `lucide-react`
 * - System-web authentication provider callbacks
 *
 * Architecture:
 * - Auth bootstrap remains the trust boundary; this component never grants access.
 * - User action delegates to MSAL login so an existing browser session can SSO.
 *
 * Notes:
 * - This screen is for recoverable session expiry only, not role or account denial.
 */

import { LockKeyhole } from 'lucide-react';

/**
 * @contract SystemSessionExpiredScreenProps
 *
 * Purpose:
 * - Defines the actions and message shown when a system-web session requires reauthentication.
 *
 * Fields:
 * - message: user-facing explanation for the expired or unverifiable session.
 * - onLogin: callback that starts the MSAL reauthentication flow.
 *
 * Rules:
 * - onLogin must not clear authorization-denied state; it only retries authentication.
 *
 * Sync:
 * - Local UI state only; no Dexie or server synchronization occurs here.
 */
interface SystemSessionExpiredScreenProps {
  message: string;
  onLogin: () => void;
}

/**
 * @component SystemSessionExpiredScreen
 *
 * Purpose:
 * - Gives system administrators a clear path to reauthenticate after session expiry.
 *
 * Props:
 * - message: display-safe session expiry details.
 * - onLogin: starts a fresh MSAL login redirect.
 *
 * Behavior:
 * - Renders a full-screen prompt with a Sign In Again action.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must not call APIs or start local-first sync directly.
 *
 * Performance:
 * - Stateless render-only component.
 */
export default function SystemSessionExpiredScreen({ message, onLogin }: SystemSessionExpiredScreenProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="max-w-md px-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-100">
          <LockKeyhole className="h-7 w-7 text-orange-600" />
        </div>
        <h1 className="mb-2 text-xl font-bold text-gray-900">Session Expired</h1>
        <p className="mb-4 text-sm text-gray-600">{message}</p>
        <p className="mb-6 text-xs text-gray-400">
          Sign in again to continue. If your Microsoft session is still active, this should complete automatically.
        </p>
        <button
          type="button"
          onClick={onLogin}
          className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700"
        >
          Sign In Again
        </button>
      </div>
    </div>
  );
}
